import { useEffect, useMemo, useState } from 'react';
import { Loader2, Check, RotateCcw } from 'lucide-react';
import { api } from '../lib/spizirnaUi';

/**
 * Správa → Provozovna → Průvodce hovorem (2026-10-22): věty, které obsluha
 * říká do telefonu — pozdrav, otázka na jméno a na konci jedna věta: přijde
 * SMS + rozloučení (od 2026-10-26). Server je skládá (VetyHovoru.php), tady
 * se jen píše text se značkami. Vpravo ukázka celého hovoru s vyplněnými značkami. SMS, které
 * hovor pošle, jsou ve Správě → Integrace → SMS → Šablony.
 */
const SECTION = '#6C4192';

// Stejně jako VetyHovoru::vyplnit — prázdná značka zmizí i s čárkou.
export const vyplnitVetu = (text, hodnoty) => text
    .replace(/\{([a-z_]+)\}/g, (_, z) => hodnoty[z] ?? '')
    .replace(/\s+/g, ' ')
    .replace(/,\s*(?=[,.!?])/g, '')
    .replace(/\s+([,.!?])/g, '$1')
    .trim();

export default function VetyHovoru({ isDark }) {
    const [data, setData] = useState(null);
    const [texty, setTexty] = useState({});
    const [hlaska, setHlaska] = useState(3);
    const [zmeneno, setZmeneno] = useState(false);
    const [uklada, setUklada] = useState(false);
    const [chyba, setChyba] = useState('');
    const [ulozeno, setUlozeno] = useState(false);

    const prijmi = (d) => {
        setData(d);
        setTexty(Object.fromEntries(d.vety.map((v) => [v.id, v.text])));
        setHlaska(d.hlaska_s);
        setZmeneno(false);
    };
    useEffect(() => { api('/api/pruvodce-hovoru/vety').then(prijmi).catch(() => setChyba('Věty se nepodařilo načíst.')); }, []);

    const muted = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const fieldBg = isDark ? 'rgba(255,255,255,0.04)' : '#fff';
    const plocha = isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)';
    const akcentText = isDark ? '#C4A3E3' : SECTION;
    const mono = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

    const zmen = (id, text) => { setTexty((t) => ({ ...t, [id]: text })); setZmeneno(true); setUlozeno(false); };

    const rozbor = useMemo(() => {
        if (!data) return {};
        const ukazka = data.ukazka;
        return Object.fromEntries(data.vety.map((v) => {
            const t = texty[v.id] ?? '';
            const nezname = [...t.matchAll(/\{([a-z_]+)\}/g)].map((m) => m[1]).filter((z) => !v.znacky.includes(z));
            return [v.id, { nahled: vyplnitVetu(t, ukazka), nezname, prazdny: !t.trim(), ok: t.trim() && !nezname.length }];
        }));
    }, [data, texty]);

    const ulozit = async () => {
        setChyba('');
        setUklada(true);
        try {
            prijmi(await api('/api/pruvodce-hovoru/vety', 'PUT', { vety: Object.entries(texty).map(([id, text]) => ({ id, text })), hlaska_s: Number(hlaska) || 0 }));
            setUlozeno(true);
        } catch (e) {
            const zpravy = e?.errors ? Object.values(e.errors).flat().join(' ') : null;
            setChyba(zpravy || e?.message || 'Uložit se nepodařilo.');
        } finally {
            setUklada(false);
        }
    };

    if (!data) {
        return chyba
            ? <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>
            : <Loader2 size={18} className="animate-spin" style={{ color: muted }} />;
    }

    const vsechnoOk = Object.values(rozbor).every((r) => r.ok);
    const n = (id) => rozbor[id]?.nahled;
    // Ukázka hovoru: rozvoz pro Petra — konec je jedna věta (SMS + rozloučení)
    const hovor = [
        { kdo: 'obsluha', text: n('pozdrav_den') },
        { kdo: 'zakaznik', text: 'Dobrý den, chtěl bych si objednat…' },
        { kdo: 'obsluha', text: n('zaver_rozvoz') },
    ];

    return (
        <div className="max-w-5xl" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="text-xs" style={{ color: muted, margin: 0, maxWidth: 560 }}>
                    Věty, které pokladna při hovoru ukáže obsluze k řečení. Značky ve složených závorkách doplní systém podle objednávky;
                    když hodnota chybí (nové číslo bez jména), zmizí i s čárkou. SMS, které hovor pošle, upravíte v Integrace → SMS → Šablony.
                </p>
                <div className="flex items-center gap-3">
                    {zmeneno && <span className="text-xs font-semibold" style={{ color: '#F59E0B' }}>Neuložené změny</span>}
                    {ulozeno && !zmeneno && <span className="text-xs font-semibold flex items-center gap-1" style={{ color: '#10b981' }}><Check size={13} /> Uloženo</span>}
                    <button type="button" onClick={ulozit} disabled={!zmeneno || !vsechnoOk || uklada}
                        className="text-xs font-semibold rounded-lg flex items-center gap-1.5"
                        style={{ height: 34, padding: '0 14px', border: 'none', cursor: zmeneno && vsechnoOk ? 'pointer' : 'default',
                            background: zmeneno && vsechnoOk ? SECTION : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
                            color: zmeneno && vsechnoOk ? '#fff' : muted }}>
                        {uklada && <Loader2 size={13} className="animate-spin" />} Uložit změny
                    </button>
                </div>
            </div>
            {chyba && <p className="text-xs" style={{ color: '#EF4444', margin: 0 }}>{chyba}</p>}

            <div className="flex flex-wrap items-start" style={{ gap: 14 }}>
                <div style={{ flex: '999 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <section className="rounded-xl flex flex-wrap items-center justify-between gap-3" style={{ padding: 14, background: plocha, border: `1px solid ${border}` }}>
                        <div>
                            <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Úvodní hláška</div>
                            <div className="text-xs mt-0.5" style={{ color: muted }}>Jak dlouho po zvednutí hraje hláška o nahrávání — pokladna mezitím ukáže „Počkej“.</div>
                        </div>
                        <label className="flex items-center gap-2 text-xs" style={{ color: muted }}>
                            <input type="number" min={0} max={20} value={hlaska} onChange={(e) => { setHlaska(e.target.value); setZmeneno(true); setUlozeno(false); }}
                                className="rounded-lg text-sm text-right" style={{ width: 64, height: 34, padding: '0 8px', background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                            s
                        </label>
                    </section>

                    {Object.entries(data.skupiny).map(([sk, nazevSkupiny]) => (
                        <div key={sk} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <h4 className="text-xs font-bold uppercase" style={{ color: muted, letterSpacing: '0.06em', margin: '2px 0 0' }}>{nazevSkupiny}</h4>
                            {data.vety.filter((v) => v.skupina === sk).map((v) => {
                                const r = rozbor[v.id];
                                const text = texty[v.id] ?? '';
                                return (
                                    <section key={v.id} className="rounded-xl" style={{ padding: 14, background: plocha, border: `1px solid ${r.ok ? border : '#EF4444'}` }}>
                                        <div className="flex flex-wrap items-start justify-between gap-2">
                                            <div>
                                                <label htmlFor={`veta-${v.id}`} className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{v.nazev}</label>
                                                <div className="text-xs mt-0.5" style={{ color: muted }}>{v.kdy}</div>
                                            </div>
                                            {text !== v.vychozi && (
                                                <button type="button" onClick={() => zmen(v.id, v.vychozi)} title={`Výchozí: ${v.vychozi}`}
                                                    className="text-xs flex items-center gap-1" style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, minHeight: 28 }}>
                                                    <RotateCcw size={12} /> Výchozí
                                                </button>
                                            )}
                                        </div>
                                        <textarea id={`veta-${v.id}`} value={text} rows={text.length > 70 ? 2 : 1}
                                            onChange={(e) => zmen(v.id, e.target.value)}
                                            className="w-full rounded-lg text-sm"
                                            style={{ ...mono, fontSize: 13, lineHeight: 1.55, padding: '8px 12px', marginTop: 8, background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)', resize: 'vertical' }} />
                                        {v.znacky.length > 0 && (
                                            <div className="flex flex-wrap items-center gap-1.5" style={{ marginTop: 6 }}>
                                                <span className="text-xs" style={{ color: muted }}>Vložit:</span>
                                                {v.znacky.map((z) => (
                                                    <button key={z} type="button" title={data.znacky[z]} onClick={() => zmen(v.id, `${text}${text.endsWith(' ') ? '' : ' '}{${z}}`)}
                                                        className="rounded-md text-xs"
                                                        style={{ ...mono, height: 26, padding: '0 8px', cursor: 'pointer', border: `1px solid ${isDark ? '#3A2C48' : '#DCD3E6'}`, background: isDark ? '#1E1726' : '#F7F2FB', color: akcentText }}>
                                                        {`{${z}}`}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {r.ok && v.znacky.length > 0 && <p className="text-xs" style={{ color: muted, margin: '8px 0 0' }}>Ukázka: „{r.nahled}“</p>}
                                        {r.prazdny && <p className="text-xs" style={{ color: '#EF4444', margin: '8px 0 0' }}>Věta nesmí být prázdná.</p>}
                                        {r.nezname.length > 0 && <p className="text-xs" style={{ color: '#EF4444', margin: '8px 0 0' }}>Značku {r.nezname.map((z) => `{${z}}`).join(', ')} tahle věta nezná.</p>}
                                    </section>
                                );
                            })}
                        </div>
                    ))}
                </div>

                <aside style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12, position: 'sticky', top: 0 }}>
                    <section className="rounded-xl" style={{ padding: 16, background: plocha, border: `1px solid ${border}` }}>
                        <h4 className="text-xs font-bold uppercase" style={{ color: muted, letterSpacing: '0.06em', margin: 0 }}>Ukázka hovoru</h4>
                        <div className="text-xs" style={{ color: muted, marginTop: 4 }}>Rozvoz pro stálého zákazníka, platba kartou</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                            {hovor.map((b, i) => (
                                <div key={i} style={{
                                    alignSelf: b.kdo === 'obsluha' ? 'flex-start' : 'flex-end', maxWidth: '88%',
                                    background: b.kdo === 'obsluha' ? (isDark ? '#2A2233' : '#F3EEF8') : (isDark ? 'rgba(255,255,255,0.06)' : '#F0EEEB'),
                                    color: 'var(--text-primary)', borderRadius: b.kdo === 'obsluha' ? '14px 14px 14px 4px' : '14px 14px 4px 14px',
                                    padding: '8px 11px', fontSize: 13, lineHeight: 1.45, fontStyle: b.kdo === 'zakaznik' ? 'italic' : 'normal',
                                }}>
                                    {b.text}
                                </div>
                            ))}
                        </div>
                    </section>
                </aside>
            </div>
        </div>
    );
}
